import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import Select from 'react-select'; // Import react-select
import CopyrightIcon from '@mui/icons-material/Copyright';
import CircularProgress from '@mui/material/CircularProgress';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function CutomerDetails() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const tableRef = React.createRef();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();


    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);



    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            setLoading(true);

            try {
                const response = await fetchData('ReportApi', `/CustomerDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Customer Details:', error);
            }
            finally {
                setLoading(false); // Hide loading screen
            }
        }
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    // const handleExportExcel = () => {
    //     const excelData = [];
    //     const headerCaption = [`Customer Data From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`];
    //     excelData.push(headerCaption);
    //     const headerRow = ['Bill Number', 'Bill Date', 'Bill Time', 'Customer Name', 'Contact Number', 'Customer Address', 'Qty Billed', 'Total MRP Billed', 'Net Sales Value + Tax Billed', 'Total Discount %','Additional Disc Type','SC MembershipNumber','Voucher Number','Sales Man'];
    //     excelData.push(headerRow);
    //     filteredDataItem.forEach((rowData, index) => {
    //         const dataRow = [];
    //         dataRow.push(rowData.BillNumber, formatDate(rowData.BillDate), rowData.BillTime, rowData.CustomerName, rowData.ContactNumber, rowData.CustomerAddress, rowData.QtyBilled, rowData.MRPBilled, rowData.TotalBillAmt, rowData.TotalDiscPer,rowData.AdditionalDiscountType,rowData.SCMembershipNumber,rowData.VoucherNumber,rowData.SalesManName);
    //         excelData.push(dataRow);
    //     });

    //     // Add total row
    //     const totalRow = ['', '', '', '', '', 'Total', totalQtyBilled, totalMRPBilled, totalTotalBillAmt, ''];
    //     excelData.push(totalRow);

    //     const ws = XLSX.utils.aoa_to_sheet(excelData);
    //     const wb = XLSX.utils.book_new();
    //     XLSX.utils.book_append_sheet(wb, ws, 'Customer Data');
    //     XLSX.writeFile(wb, 'Customer_Data.xlsx');
    // };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Customer Data');

        const rightAlignColumns = [
            'Qty Billed', 'Total MRP Billed', 'Net Sales Value + Tax Billed', 'Total Discount %'
        ];

        // Caption
        const headerCaption = `Customer Data From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        worksheet.mergeCells('A1:N1');
        const headerCaptionCell = worksheet.getCell('A1');
        headerCaptionCell.value = headerCaption;
        headerCaptionCell.font = { bold: true, size: 14 };
        headerCaptionCell.alignment = { vertical: 'middle', horizontal: 'left' };

        // Header row
        const headerRowValues = [
            'Bill Number', 'Bill Date', 'Bill Time', 'Customer Name', 'Contact Number', 'Customer Address',
            'Qty Billed', 'Total MRP Billed', 'Net Sales Value + Tax Billed', 'Total Discount %',
            'Additional Disc Type', 'SC Membership Number', 'Voucher Number', 'Sales Man'
        ];
        const headerRow = worksheet.addRow(headerRowValues);

        headerRow.eachCell((cell) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFB6D7A8' }
            };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));

        // Data rows
        filteredDataItem.forEach((rowData) => {
            const dataRow = [
                rowData.BillNumber,
                formatDate(rowData.BillDate),
                rowData.BillTime,
                rowData.CustomerName,
                rowData.ContactNumber,
                rowData.CustomerAddress,
                parseFloat(rowData.QtyBilled),
                parseFloat(rowData.MRPBilled),
                parseFloat(rowData.TotalBillAmt),
                parseFloat(rowData.TotalDiscPer),
                rowData.AdditionalDiscountType,
                rowData.SCMembershipNumber,
                rowData.VoucherNumber,
                rowData.SalesManName
            ];

            const row = worksheet.addRow(dataRow);

            row.eachCell((cell, colNumber) => {
                const headerName = headerRowValues[colNumber - 1];
                if (rightAlignColumns.includes(headerName)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                }
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });
        });

        // Total row
        const totalRowValues = [
            '', '', '', '', '', 'Total',
            parseFloat(totalQtyBilled),
            parseFloat(totalMRPBilled),
            parseFloat(totalTotalBillAmt),
            '', '', '', '', ''
        ];
        const totalRow = worksheet.addRow(totalRowValues);
        totalRow.font = { bold: true };

        totalRow.eachCell((cell, colNumber) => {
            const headerName = headerRowValues[colNumber - 1];
            if (rightAlignColumns.includes(headerName)) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
            } else {
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }

            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFF2CC' }
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // Save file
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'Customer_Data.xlsx');
    };


    const [totalQtyBilled, setTotalQtyBilled] = useState(0);
    const [totalMRPBilled, setTotalMRPBilled] = useState(0);
    const [totalTotalBillAmt, setTotalTotalBillAmt] = useState(0);

    // useEffect(() => {
    //     // Calculate totals        
    //     let QtyBilled = 0;
    //     let MRPBilled = 0;
    //     let TotalBillAmt = 0;

    //     dataItem.forEach(item => {
    //         QtyBilled += item.QtyBilled;
    //         MRPBilled += item.MRPBilled;
    //         TotalBillAmt += item.TotalBillAmt;
    //     });

    //     setTotalQtyBilled(QtyBilled);
    //     setTotalMRPBilled(MRPBilled);
    //     setTotalTotalBillAmt(TotalBillAmt);
    // }, [dataItem]);

    useEffect(() => {
        // Calculate totals        
        let QtyBilled = 0;
        let MRPBilled = 0;
        let TotalBillAmt = 0;

        dataItem.forEach(item => {
            QtyBilled += Number(item?.QtyBilled ?? 0);
            MRPBilled += Number(item?.MRPBilled ?? 0);
            TotalBillAmt += Number(item?.TotalBillAmt ?? 0);
        });

        setTotalQtyBilled(QtyBilled.toFixed(2));
        setTotalMRPBilled(MRPBilled.toFixed(2));
        setTotalTotalBillAmt(TotalBillAmt.toFixed(2));
    }, [dataItem]);


    return (
        <div className="w-full  mx-auto" style={{ width: '95%', marginTop: '10px' }}>
            <div style={{ width: '100%', marginBottom: '2', display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                <div className='w-full mb-2 flex' style={{ marginTop: '10px' }}>
                    <h6 className="block uppercase tracking-wide text-black productHead flex-grow linkcolor" style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif', color: 'black' }}>
                        Customer Data
                    </h6>
                </div>

                <div className='w-full mb-2 flex' style={{ marginTop: '10px' }}>
                    {loading && (
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 9999
                        }}>
                            <div>
                                <CircularProgress style={{ color: 'white' }} />
                                <span style={{ color: 'white', marginTop: '10px', display: 'block' }}>Loading...</span>
                            </div>
                        </div>
                    )}
                    <Button
                        style={{ color: "black", height: '36px', marginLeft: '10px' }}
                        onClick={fetchTableData}
                        className="linkcolor"
                    >
                        <IconButton onClick={fetchTableData}>
                            <RefreshIcon style={{ color: 'brown' }} />
                        </IconButton>
                        <span style={{ color: 'brown', fontWeight: 'bolder' }}>Refresh</span>
                    </Button>
                    <Button
                        style={{ color: "black", height: '36px', marginLeft: '10px' }}
                        onClick={handleExportExcel}
                        className="linkcolor"
                    >
                        <IconButton onClick={handleExportExcel}>
                            <FeedIcon style={{ color: 'darkgreen' }} />
                        </IconButton>
                        <span style={{ color: 'darkgreen', fontWeight: 'bolder' }}>Excel</span>
                    </Button>

                    <div className="watermark">
                        <CopyrightIcon /><b style={{ fontFamily: 'Script Bold Italic' }}>EFFTABS SOLUTIONS LLP</b>
                    </div>

                    <div style={{ marginBottom: '10px', marginLeft: '20px', display: 'flex', alignItems: 'center' }}>
                        <SearchIcon onClick={toggleSearchInput} style={{ cursor: 'pointer' }} />
                        {showSearchInput && (
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={handleSearch}
                                style={{ marginLeft: '10px' }}
                            />
                        )}
                    </div>
                </div>

                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        FromDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={fromDate} onChange={handleFromDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        ToDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={toDate} onChange={handleToDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
            </div>

            <div className='w-full mb-5 flex' style={{ maxHeight: '550px', overflowY: 'auto', marginTop: '7px' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#08396B', Width: '2500px' }}>
                            <TableRow sx={{ whiteSpace: 'nowrap' }}>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Bill Number</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Bill Date</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Bill Time</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Customer Name</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Contact Number</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Customer Address</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Qty Billed</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Total MRP Billed</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Net Sales Value + Tax Billed</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Total Discount %</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Addt'l. DISC Type</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>SC MembershipNumber</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Voucher Number</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Sales Man</TableCell>
                            </TableRow>
                        </TableHead>

                        {/* <TableBody sx={{ backgroundColor: '#24203C' }}> */}
                        <TableBody sx={{ backgroundColor: 'whitesmoke' }}>
                            {filteredDataItem.map((data, index) => (
                                <TableRow
                                    key={data.id}
                                    className={data.routeName === 'Total' ? 'highlighted-row' : ''}
                                    sx={{
                                        padding: '7px',
                                        height: '40px',
                                        backgroundColor: data.routeName === 'Total' ? 'teal' : 'transparent',

                                    }}
                                >
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.BillNumber}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{formatDate(data.BillDate)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.BillTime}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.CustomerName}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.ContactNumber}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.CustomerAddress}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>
                                        {Number(data?.QtyBilled ?? 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>
                                        {Number(data?.MRPBilled ?? 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>
                                        {Number(data?.TotalBillAmt ?? 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>
                                        {Number(data?.TotalDiscPer ?? 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>
                                        {data?.AdditionalDiscountType ?? ""}
                                    </TableCell>

                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.SCMembershipNumber}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.VoucherNumber}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '12px' }}>{data.SalesManName}</TableCell>
                                </TableRow>
                            ))}

                            <TableRow
                                className={'highlighted-row'}
                                sx={{
                                    padding: '7px',
                                    height: '40px',

                                    backgroundColor: '#336D82',

                                }}
                            >
                                <TableCell colSpan={5} />
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'right', fontSize: '14px', textTransform: 'uppercase' }}>Total Bills</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '14px' }}>{totalQtyBilled}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '14px' }}>{totalMRPBilled}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'center', fontSize: '14px' }}>{totalTotalBillAmt}</TableCell>
                                <TableCell colSpan={5} />
                            </TableRow>

                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}

export default CutomerDetails;
